/**
 * Labstep
 *
 * @module components/UserGroupNotification/Table
 */

import { EntityActionUpdateContainer as EntityActionUpdate } from 'labstep-web/components/Entity/Action/Update';
import { UserGroupNotificationActionUpdateMode } from 'labstep-web/components/UserGroupNotification/Action/UpdateMode';
import { NOTIFICATIONS_DEFINITIONS } from 'labstep-web/constants/user-group-notification';
import { TableSimpleCollapsible } from 'labstep-web/core/Table/Simple/Collapsible';
import { usePremiumFeatureStatus } from 'labstep-web/hooks/premium-feature';
import type { NotificationType } from 'labstep-web/models/notification.model';
import { UserGroupNotification } from 'labstep-web/models/user-group-notification.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import type React from 'react';
import type {
  IUserGroupNotificationTableProps,
  NotificationRow,
} from './types';

export const columns = [
  {
    header: 'Notify me when...',
    content: (notificationType: NotificationRow) => (
      <span style={{ marginLeft: '50px' }}>
        {notificationType.name}
      </span>
    ),
    cellProps: { width: 9, 'data-public': true },
  },
  {
    header: 'for...',
    content: (notificationType: NotificationRow) => {
      const typesWithSettings = Object.keys(
        NOTIFICATIONS_DEFINITIONS,
      ).reduce((result, key) => {
        const values = Object.keys(
          NOTIFICATIONS_DEFINITIONS[key],
        ).map((subkey: string) => ({
          ...NOTIFICATIONS_DEFINITIONS[key][subkey],
          key: subkey,
        }));

        return [...result, ...values];
      }, []);
      const setting = typesWithSettings.find(
        (typeWithSettings) =>
          typeWithSettings.key ===
          notificationType.userGroupNotification.notification_type,
      );

      if (setting && setting.mode === true) {
        return (
          <UserGroupNotificationActionUpdateMode
            userGroupNotification={
              notificationType.userGroupNotification
            }
          />
        );
      }

      return null;
    },
    cellProps: { style: { width: 300, 'data-public': true } },
  },
  {
    header: 'via web app',
    content: (notificationType: NotificationRow) => (
      <EntityActionUpdate
        optimistic
        entityName={UserGroupNotification.entityName}
        id={notificationType.userGroupNotification.id}
        body={{
          is_push: !notificationType.userGroupNotification.is_push,
        }}
        actionComponentProps={{
          type: 'checkbox',
          elementProps: {
            checked: notificationType.userGroupNotification.is_push,
            toggle: false,
          },
        }}
      />
    ),
    cellProps: { style: { width: 100, 'data-public': true } },
  },
  {
    header: 'via email',
    content: (notificationType: NotificationRow) => (
      <EntityActionUpdate
        optimistic
        entityName={notificationType.userGroupNotification.entityName}
        id={notificationType.userGroupNotification.id}
        body={{
          is_email: !notificationType.userGroupNotification.is_email,
        }}
        actionComponentProps={{
          type: 'checkbox',
          elementProps: {
            checked: notificationType.userGroupNotification.is_email,
            toggle: false,
          },
        }}
      />
    ),
    cellProps: { style: { width: 100, 'data-public': true } },
  },
];

export const getSections = (
  userGroupNotifications: UserGroupNotification[],
) => {
  const sections = Object.keys(NOTIFICATIONS_DEFINITIONS).map(
    (notificationCategory) => ({
      entityName: notificationCategory,
      collapsible: true,
      name: getHumanReadableEntityName(
        notificationCategory,
        true,
        true,
      ),
      rows: Object.keys(
        NOTIFICATIONS_DEFINITIONS[notificationCategory],
      )
        .filter(
          (notificationType) =>
            !!userGroupNotifications.find(
              (u) => u.notification_type === notificationType,
            ),
        )
        .map(
          (notificationType): NotificationRow => ({
            name:
              NOTIFICATIONS_DEFINITIONS[notificationCategory][
                notificationType as NotificationType
              ]?.label || '',
            userGroupNotification: userGroupNotifications.find(
              (u) => u.notification_type === notificationType,
            )!,
          }),
        ),
    }),
  );

  const premiumFilteredSections = usePremiumFeatureStatus(
    sections,
    'entityName',
    'categories',
  );

  return premiumFilteredSections;
};

export const UserGroupNotificationTable: React.FC<
  IUserGroupNotificationTableProps
> = ({ userGroupNotifications }) => {
  return (
    <TableSimpleCollapsible
      columns={columns}
      sections={getSections(userGroupNotifications)}
    />
  );
};
